import React, { useMemo, useState } from 'react';
import { IStyleSettingColors } from 'types';
import { SegmentedSwitch } from 'components/SegmentedSwitch/SegmentedSwitch';
import { TabItem } from 'components/Tabs/TabItem';
import { Tabs } from 'components/Tabs/Tabs';
import Text from 'components/Text/Text';
import { StyleInputNew } from './components/StyleInputNew';
import { StyleSectionNew } from './components/StyleSectionNew';
import classNames from 'classnames';

type TColorsType = 'general' | 'additional';

const colorsTypeOptions: { value: TColorsType; translationKey: string }[] = [
	{ value: 'general', translationKey: 'generalColors' },
	{ value: 'additional', translationKey: 'additionalColors' },
];

interface IProps {
	styleSettingColors: IStyleSettingColors;
	setStyleSettings: (styleItemId: string, value: string, key: string) => void;
}

export const StyleFieldsNew: React.FC<IProps> = ({ styleSettingColors, setStyleSettings }) => {
	const { additionalColors } = styleSettingColors;
	const [activeColors, setActiveColors] = useState<TColorsType>('general');
	const [selectedNavigationItem, setSelectedNavigationItem] = useState<Nullable<string>>(
		additionalColors && Object.keys(additionalColors)[0],
	);

	const navigationItems = useMemo(() => (additionalColors ? Object.keys(additionalColors) : []), [additionalColors]);

	return (
		<div className="flex flex-col gap-4" data-test="StyleFields">
			<div>
				<SegmentedSwitch
					value={activeColors}
					options={colorsTypeOptions}
					translationNameSpace="mediaSettings"
					testId="ColorsTypeSwitch"
					onChange={setActiveColors}
				/>
			</div>
			<div className={classNames('-mx-4 flex flex-wrap gap-y-4', { hidden: activeColors !== 'general' })}>
				{styleSettingColors.generalColors.map((el) => (
					<div key={el.id} className="w-1/3 px-4">
						<StyleInputNew data={el} setStyleSettings={setStyleSettings} />
					</div>
				))}
			</div>
			<div className={classNames('flex flex-col gap-4', { hidden: activeColors !== 'additional' })}>
				<Tabs>
					{navigationItems.map((el) => (
						<TabItem
							key={el}
							label={<Text translationNameSpace="mediaSettings" translationKey={el} asFragment={true} />}
							isActive={el === selectedNavigationItem}
							dataTest={`AdditionalColorsTab-${el}`}
							onClick={() => setSelectedNavigationItem(el)}
						/>
					))}
				</Tabs>
				{additionalColors &&
					navigationItems.map((navigationItem) => (
						<div
							key={navigationItem}
							className={classNames('-mx-2 flex flex-wrap gap-y-4', {
								hidden: navigationItem !== selectedNavigationItem,
							})}
							data-test={`AdditionalColors-${navigationItem}`}>
							{Object.keys(additionalColors[navigationItem]).map((sectionKey) => (
								<StyleSectionNew
									key={sectionKey}
									sectionKey={sectionKey}
									colors={additionalColors[navigationItem][sectionKey]}
									setStyleSettings={setStyleSettings}
								/>
							))}
						</div>
					))}
			</div>
		</div>
	);
};
